/**
 * What the trainer is told after the plan lands.
 *
 * ── The counts are the SERVER's, and they are optional on the wire ───────────────────────────
 * `workoutCount` and `calendarEventCount` are what the backend actually materialised, which is the
 * only number worth showing: the review step's own occurrence total is this app's arithmetic, and it
 * would agree with the server right up until the one time it mattered.
 *
 * Both are `int` server-side and therefore never null in practice — but orval types every value type
 * as optional, and "in practice" is how a screen ends up rendering `undefined treningów`. They are
 * read defensively and the fallback is zero, which is a true statement about a plan that generated
 * nothing rather than a hidden one.
 *
 * ── The next step is a LINK now, and the gap this header stated is closed ────────────────────
 * It used to say there was no assigned-plans surface to point at. There has been one since
 * 14 August 2026 (`pages/assigned-plan-calendar`), and what was actually missing was an address:
 * the confirmation carried no assignment id to build one from. The POST's own response carries
 * both ids, so the link is built from it — see `calendarLink`.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. `t` is a parameter.
 */

import type { LinkProps } from '@tanstack/react-router';

import { planCalendarLink } from '~/entities/client';
import type { TrainWithMeModulesTrainingDtosResponsesPlanAssignmentFullResponse as PlanAssignmentFullResponse } from '~/shared/api/generated/model';

export type Translate = (key: string, values?: Record<string, string | number>) => string;

/** The confirmation, already worded — the `ui/` side of the seam holds no DTO. */
export interface AssignConfirmation {
  readonly title: string;
  /** "Anna Kowalska ma już ten plan w swojej aplikacji." */
  readonly body: string;
  /**
   * What landed. "12 treningów · 12 wpisów w kalendarzu" on a dated plan; on a self-paced one, a
   * sentence with no figure in it at all. See `assignConfirmation` for why the two differ in kind
   * rather than in wording — and why this is not called `counts`.
   */
  readonly summary: string;
  /**
   * Where „Otwórz kalendarz Zofii" goes, or `null` when the response did not carry both ids.
   *
   * ── This closes the gap the old `nextStep` sentence stated ──────────────────────────────────
   * That line said the client's progress could be seen "in their calendar, in the Klienci tab" and
   * left the trainer to find it. `pages/assigned-plan-calendar` has existed since 14 August and the
   * POST's own response carries the assignment id, so the destination is addressable — and
   * `planCalendarLink` is the one module that decides its address, shared with the plan card on two
   * other screens. `null` rather than a weaker link: the assignment is half the path now, so there
   * is no fallback destination, only a different one.
   */
  readonly calendarLink: LinkProps | null;
}

/**
 * What the trainer reads once the plan has landed.
 *
 * ── A self-paced assignment has NOTHING to count, so it reports no number at all ─────────────
 * It materialises no workouts and books no dates. Both halves of that are the design rather than a
 * shortfall: R8 says the calendar records what happened and never what will happen, and
 * `PlanAssignmentProcessor.EmptyRollup` names the zero-workout case "the NORM for a self-paced plan,
 * whose rows appear only as the client lives them". So `workoutCount` and `calendarEventCount` are
 * both zero on the response, and correctly so.
 *
 * Rendered through the dated sentence, that produced "0 treningów · 0 wpisów w kalendarzu" — a
 * success screen whose only figures were zeroes, standing exactly where a trainer reads the result,
 * saying the assignment failed. An earlier round replaced only the second half and left "{n}
 * treningów" in front of it, on the belief that the workouts were materialised; they are not, and
 * that belief is withdrawn here.
 *
 * The whole line is therefore a sentence with no figure in it, answering the question the zeroes
 * were accidentally raising — where is the work — rather than restating them. The step before this
 * one already said how much plan there is ("3 cykle · 6 sesji"), so nothing is lost by not repeating
 * it; what the trainer does not otherwise know is WHEN it becomes real.
 *
 * The mode arrives as a PARAMETER rather than being sniffed off the response: the flow already knows
 * it (the tree's `schedulingMode` chose which review step ran), and a confirmation inferring the
 * mode from a zero count would call a dated plan that generated nothing self-paced.
 */
export function assignConfirmation(
  result: PlanAssignmentFullResponse,
  /** The name the trainer picked in step 1; the response's own is the fallback. */
  chosenName: string | null,
  /** The mode the flow assigned on — `true` for a plan that waits for the client. */
  isSelfPaced: boolean,
  t: Translate,
): AssignConfirmation {
  const name = chosenName ?? result.client?.fullName ?? '';

  return {
    title: t('web:assign.done.title'),
    body: t('web:assign.done.body', { name }),
    summary: isSelfPaced
      ? t('web:assign.done.summarySelfPaced')
      : t('web:assign.done.counts', {
          workouts: t('web:assign.done.workouts', { count: result.workoutCount ?? 0 }),
          events: t('web:assign.done.events', { count: result.calendarEventCount ?? 0 }),
        }),
    // Built from the response rather than from flow state: the assignment id exists only on the
    // answer, and the customer id has to be the one the SERVER recorded rather than the one the
    // dialog sent, or a link would be written for a client the assignment did not go to.
    calendarLink: planCalendarLink({
      clientId: result.customerId,
      assignmentId: result.id,
    }),
  };
}
